// CompanyReviews.jsx
// Two exports:
//   <CompanyReviewsDisplay companyId={...} companyName={...} />  — public review list (used in job detail)
//   <WriteReviewModal companyId={...} companyName={...} applicationId={...} user={...} onClose={...} notify={...} />  — student submission form

import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const T = {
  primary:"#0a6e4e", primaryL:"#e8f5f0", amber:"#f5a623", amberL:"#fef9ec",
  ink:"#0f1923", inkL:"#6b7a8d", surface:"#f4f7f5", border:"#e2e8e5",
  white:"#fff", red:"#dc2626", redL:"#fef2f2", green:"#16a34a", greenL:"#f0fdf4",
};

// ── Star Rating Input ─────────────────────────────────────────────────────────
function StarInput({ value, onChange, size = 24 }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display:"flex", gap:4 }}>
      {[1,2,3,4,5].map(n => (
        <span key={n}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
          style={{ fontSize:size, cursor:"pointer", color:(hovered||value)>=n ? T.amber : "#d1d5db", transition:"color .1s" }}>
          ★
        </span>
      ))}
    </div>
  );
}

// ── Star Display ──────────────────────────────────────────────────────────────
function Stars({ value, size = 16 }) {
  return (
    <span style={{ letterSpacing:1 }}>
      {[1,2,3,4,5].map(n => (
        <span key={n} style={{ fontSize:size, color: value >= n ? T.amber : "#d1d5db" }}>★</span>
      ))}
    </span>
  );
}

// ── Rating Bar ────────────────────────────────────────────────────────────────
function RatingBar({ label, value }) {
  if (!value) return null;
  const pct = (value / 5) * 100;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
      <div style={{ width:110, fontSize:12, color:T.inkL, flexShrink:0 }}>{label}</div>
      <div style={{ flex:1, height:6, background:T.border, borderRadius:100, overflow:"hidden" }}>
        <div style={{ width:`${pct}%`, height:"100%", background:T.primary, borderRadius:100 }} />
      </div>
      <div style={{ width:28, fontSize:12, fontWeight:700, color:T.ink, textAlign:"right" }}>{value}</div>
    </div>
  );
}

// ── Write Review Modal ────────────────────────────────────────────────────────
export function WriteReviewModal({ companyId, companyName, applicationId, user, onClose, notify }) {
  const [form, setForm] = useState({
    rating_overall: 0, rating_mentorship: 0, rating_work_life: 0,
    rating_learning: 0, rating_culture: 0,
    headline: "", body: "", pros: "", cons: "",
    would_recommend: true, is_anonymous: false,
  });
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    supabase.from("company_reviews")
      .select("*").eq("student_id", user.id).eq("company_id", companyId)
      .maybeSingle()
      .then(({ data }) => { if (data) { setExisting(data); setForm({ ...data }); } });
  }, [user.id, companyId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (form.rating_overall === 0) { notify("Please give an overall rating", false); return; }
    if (!form.headline.trim()) { notify("Please add a headline", false); return; }
    setSaving(true);
    const payload = {
      company_id: companyId,
      student_id: user.id,
      application_id: applicationId || null,
      ...form,
    };
    const { error } = existing
      ? await supabase.from("company_reviews").update(payload).eq("id", existing.id)
      : await supabase.from("company_reviews").insert(payload);
    setSaving(false);
    if (error) { notify("Failed: " + error.message, false); return; }
    notify("Review submitted! Thank you for helping future interns. ✓");
    onClose();
  };

  const CATS = [
    { key:"rating_mentorship", label:"Mentorship & Guidance" },
    { key:"rating_work_life",  label:"Work-Life Balance" },
    { key:"rating_learning",   label:"Learning Opportunities" },
    { key:"rating_culture",    label:"Company Culture" },
  ];

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:520, width:"100%", maxHeight:"90vh", overflowY:"auto" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <div>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:20, fontWeight:900, margin:0 }}>
              {existing ? "Edit Your Review" : "Write a Review"}
            </h2>
            <div style={{ fontSize:13, color:T.inkL, marginTop:2 }}>{companyName}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:T.inkL }}>✕</button>
        </div>

        {/* Overall rating */}
        <div style={{ background:T.primaryL, borderRadius:12, padding:16, marginBottom:16, textAlign:"center" }}>
          <div style={{ fontSize:13, fontWeight:700, color:T.primary, marginBottom:10 }}>Overall Rating *</div>
          <StarInput value={form.rating_overall} onChange={v => set("rating_overall", v)} size={36} />
          {form.rating_overall > 0 && (
            <div style={{ fontSize:12, color:T.inkL, marginTop:8 }}>
              {["","Poor","Fair","Good","Very Good","Excellent"][form.rating_overall]}
            </div>
          )}
        </div>

        {/* Category ratings */}
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:13, fontWeight:700, color:T.ink, marginBottom:10 }}>Rate by Category</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            {CATS.map(({ key, label }) => (
              <div key={key} style={{ background:T.surface, borderRadius:10, padding:"10px 14px" }}>
                <div style={{ fontSize:11, color:T.inkL, marginBottom:6 }}>{label}</div>
                <StarInput value={form[key]} onChange={v => set(key, v)} size={20} />
              </div>
            ))}
          </div>
        </div>

        {/* Headline */}
        <div style={{ marginBottom:12 }}>
          <label style={{ fontSize:12, fontWeight:700, color:T.ink, display:"block", marginBottom:4 }}>
            Headline *
          </label>
          <input className="input" placeholder="e.g. Great learning experience, supportive team"
            value={form.headline} onChange={e => set("headline", e.target.value)}
            maxLength={100} />
        </div>

        {/* Body */}
        <div style={{ marginBottom:12 }}>
          <label style={{ fontSize:12, fontWeight:700, color:T.ink, display:"block", marginBottom:4 }}>
            Your Experience
          </label>
          <textarea className="input" rows={4} placeholder="Share your OJT experience to help future interns make better decisions…"
            value={form.body} onChange={e => set("body", e.target.value)}
            style={{ resize:"vertical", lineHeight:1.65 }} />
        </div>

        {/* Pros / Cons */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
          <div>
            <label style={{ fontSize:12, fontWeight:700, color:T.green, display:"block", marginBottom:4 }}>👍 Pros</label>
            <textarea className="input" rows={3} placeholder="What did you love?"
              value={form.pros} onChange={e => set("pros", e.target.value)}
              style={{ resize:"none", fontSize:13 }} />
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:700, color:T.red, display:"block", marginBottom:4 }}>👎 Cons</label>
            <textarea className="input" rows={3} placeholder="What could improve?"
              value={form.cons} onChange={e => set("cons", e.target.value)}
              style={{ resize:"none", fontSize:13 }} />
          </div>
        </div>

        {/* Recommend + Anonymous */}
        <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:20 }}>
          <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", fontSize:14 }}>
            <input type="checkbox" checked={form.would_recommend}
              onChange={e => set("would_recommend", e.target.checked)}
              style={{ width:16, height:16, accentColor:T.primary }} />
            <span>I would recommend this company to other interns</span>
          </label>
          <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", fontSize:14, color:T.inkL }}>
            <input type="checkbox" checked={form.is_anonymous}
              onChange={e => set("is_anonymous", e.target.checked)}
              style={{ width:16, height:16, accentColor:T.primary }} />
            <span>Post anonymously (your name won't be shown)</span>
          </label>
        </div>

        <div style={{ display:"flex", gap:10 }}>
          <button className="btn btn-ghost" style={{ flex:1 }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex:2 }} onClick={submit} disabled={saving}>
            {saving ? "Submitting…" : existing ? "Update Review" : "Submit Review →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Company Reviews Display ───────────────────────────────────────────────────
export function CompanyReviewsDisplay({ companyId, companyName, user, notify }) {
  const [reviews, setReviews]   = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [showWrite, setShowWrite] = useState(false);
  const [expanded, setExpanded]  = useState(null);

  const load = async () => {
    setLoading(true);
    const [{ data: revs }, { data: sum }] = await Promise.all([
      supabase.from("company_reviews")
        .select("*, reviewer:profiles!company_reviews_student_id_fkey(full_name, avatar_url)")
        .eq("company_id", companyId)
        .eq("is_approved", true)
        .order("created_at", { ascending: false })
        .limit(20),
      supabase.from("company_rating_summary")
        .select("*").eq("company_id", companyId).maybeSingle(),
    ]);
    setReviews(revs || []);
    setSummary(sum);
    setLoading(false);
  };

  useEffect(() => { load(); }, [companyId]);

  const hasReviewed = reviews.some(r => r.student_id === user?.id);

  if (loading) return (
    <div style={{ textAlign:"center", padding:40, color:T.inkL, fontSize:14 }}>Loading reviews…</div>
  );

  return (
    <div>
      {/* Summary card */}
      {summary && summary.review_count > 0 ? (
        <div style={{ background:T.surface, borderRadius:14, padding:20, marginBottom:20, display:"flex", gap:24, flexWrap:"wrap" }}>
          <div style={{ textAlign:"center", minWidth:100 }}>
            <div style={{ fontSize:48, fontWeight:900, color:T.ink, lineHeight:1 }}>{summary.avg_overall}</div>
            <Stars value={Math.round(summary.avg_overall)} size={18} />
            <div style={{ fontSize:12, color:T.inkL, marginTop:4 }}>{summary.review_count} review{summary.review_count !== 1 ? "s" : ""}</div>
            {summary.recommend_count > 0 && (
              <div style={{ fontSize:11, color:T.green, fontWeight:700, marginTop:4 }}>
                👍 {Math.round((summary.recommend_count / summary.review_count) * 100)}% recommend
              </div>
            )}
          </div>
          <div style={{ flex:1, minWidth:200 }}>
            <RatingBar label="Mentorship"   value={summary.avg_mentorship} />
            <RatingBar label="Work-Life"    value={summary.avg_work_life} />
            <RatingBar label="Learning"     value={summary.avg_learning} />
            <RatingBar label="Culture"      value={summary.avg_culture} />
          </div>
        </div>
      ) : (
        <div style={{ background:T.surface, borderRadius:14, padding:24, marginBottom:20, textAlign:"center" }}>
          <div style={{ fontSize:32, marginBottom:8 }}>💬</div>
          <div style={{ fontWeight:700, color:T.ink, marginBottom:4 }}>No reviews yet</div>
          <div style={{ fontSize:13, color:T.inkL }}>Be the first to review {companyName}!</div>
        </div>
      )}

      {/* Write review button */}
      {user && !hasReviewed && (
        <button className="btn btn-primary" style={{ width:"100%", marginBottom:16 }}
          onClick={() => setShowWrite(true)}>
          ✍️ Write a Review
        </button>
      )}
      {user && hasReviewed && (
        <button className="btn btn-ghost" style={{ width:"100%", marginBottom:16 }}
          onClick={() => setShowWrite(true)}>
          ✏️ Edit Your Review
        </button>
      )}

      {/* Review list */}
      {reviews.map(r => {
        const name = r.is_anonymous ? "Anonymous Intern" : (r.reviewer?.full_name || "Student");
        const isOpen = expanded === r.id;
        return (
          <div key={r.id} style={{ border:`1.5px solid ${T.border}`, borderRadius:12, padding:16, marginBottom:12, background:T.white }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:36, height:36, borderRadius:"50%", background:T.primaryL, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, color:T.primary, fontSize:14 }}>
                  {r.is_anonymous ? "?" : name[0]?.toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color:T.ink }}>{name}</div>
                  <div style={{ fontSize:11, color:T.inkL }}>{new Date(r.created_at).toLocaleDateString("en-PH", { year:"numeric", month:"short" })}</div>
                </div>
              </div>
              <div style={{ textAlign:"right" }}>
                <Stars value={r.rating_overall} size={14} />
                {r.would_recommend && <div style={{ fontSize:11, color:T.green, fontWeight:700, marginTop:2 }}>👍 Recommends</div>}
              </div>
            </div>

            {r.headline && <div style={{ fontWeight:700, fontSize:14, color:T.ink, marginBottom:6 }}>"{r.headline}"</div>}
            {r.body && (
              <div style={{ fontSize:13, color:"#2d3d4f", lineHeight:1.65, marginBottom:8 }}>
                {isOpen || r.body.length < 200 ? r.body : r.body.slice(0, 200) + "…"}
                {r.body.length >= 200 && (
                  <button onClick={() => setExpanded(isOpen ? null : r.id)}
                    style={{ background:"none", border:"none", color:T.primary, fontSize:12, fontWeight:700, cursor:"pointer", marginLeft:4 }}>
                    {isOpen ? "Show less" : "Read more"}
                  </button>
                )}
              </div>
            )}
            {(r.pros || r.cons) && isOpen && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginTop:8 }}>
                {r.pros && (
                  <div style={{ background:T.greenL, borderRadius:8, padding:"8px 12px" }}>
                    <div style={{ fontSize:11, fontWeight:700, color:T.green, marginBottom:4 }}>👍 Pros</div>
                    <div style={{ fontSize:12, color:"#166534" }}>{r.pros}</div>
                  </div>
                )}
                {r.cons && (
                  <div style={{ background:T.redL, borderRadius:8, padding:"8px 12px" }}>
                    <div style={{ fontSize:11, fontWeight:700, color:T.red, marginBottom:4 }}>👎 Cons</div>
                    <div style={{ fontSize:12, color:"#991b1b" }}>{r.cons}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {showWrite && (
        <WriteReviewModal
          companyId={companyId}
          companyName={companyName}
          user={user}
          onClose={() => { setShowWrite(false); load(); }}
          notify={notify}
        />
      )}
    </div>
  );
}
